"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Demo data — 3 list presets, each with sample lead rows
// ============================================================================

type ListKey = 1 | 2 | 3;

type LeadRow = {
  initials: string;
  name: string;
  region: string;
  rating: string;
};

const LEAD_ROWS: Record<ListKey, LeadRow[]> = {
  1: [
    { initials: "SN", name: "Stadtwerke Nord", region: "Hamburg", rating: "2.1" },
    { initials: "EH", name: "EnergieHaus AG", region: "Köln", rating: "2.8" },
    { initials: "WS", name: "Watt Solutions", region: "München", rating: "3.1" },
    { initials: "VD", name: "Volt Direkt", region: "Berlin", rating: "2.4" },
    { initials: "PG", name: "PowerGrid Bayern", region: "Nürnberg", rating: "1.9" },
  ],
  2: [
    { initials: "AC", name: "Acme Corp", region: "Berlin", rating: "4.6" },
    { initials: "NS", name: "Nimbus Studio", region: "Hamburg", rating: "4.3" },
    { initials: "HL", name: "Helix Labs", region: "München", rating: "4.1" },
    { initials: "BB", name: "Bluebird AG", region: "Köln", rating: "4.4" },
    { initials: "OR", name: "Orbit Analytics", region: "Frankfurt", rating: "4.5" },
  ],
  3: [
    { initials: "MW", name: "Marcus Weber · Acme Corp", region: "Mo, 10:00", rating: "Hot" },
    { initials: "TB", name: "Tom Becker · Helix Labs", region: "Mi, 14:00", rating: "Warm" },
    { initials: "LR", name: "Lisa Roth · Bluebird AG", region: "Do, 11:00", rating: "Hot" },
    { initials: "AF", name: "Ahmed Faris · Novus Digital", region: "Fr, 15:00", rating: "Hot" },
  ],
};

const LIST_KEYS: ListKey[] = [1, 2, 3];
const ROTATION_MS = 6000;

// ============================================================================
// Root Section
// ============================================================================

export const CustomListsSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <ListScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("customLists");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <FilterIcon className="h-3.5 w-3.5" />
        Custom Lists
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — List Scene
// ============================================================================

const ListScene = () => {
  const t = useTranslations("customLists");
  const [active, setActive] = React.useState<ListKey>(1);

  React.useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => {
        const i = LIST_KEYS.indexOf(prev);
        return LIST_KEYS[(i + 1) % LIST_KEYS.length];
      });
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">

      {/* Toolbar — preset selector */}
      <div className="border-b border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center gap-3 overflow-x-auto">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <BoltIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t("presets_label")}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-1">
          {LIST_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setActive(key)}
              className={cn(
                "text-[11px] font-medium px-2.5 py-1 rounded-md transition-colors whitespace-nowrap",
                active === key
                  ? "bg-red-500/15 text-red-200 ring-1 ring-red-500/30"
                  : "text-neutral-500 hover:text-neutral-300"
              )}
            >
              {t(`list_${key}_name`)}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-neutral-500 hidden md:inline whitespace-nowrap">
          {t("presets_meta")}
        </span>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[540px]">
        <FilterBuilder active={active} />
        <PreviewPane active={active} />
      </div>

      {/* Footer */}
      <div className="border-t border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] text-neutral-500">{t("footer_meta")}</span>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Filter builder (left)
// ----------------------------------------------------------------------------

const FilterBuilder = ({ active }: { active: ListKey }) => {
  const t = useTranslations("customLists");
  return (
    <div className="lg:col-span-5 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/40 p-5 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {t(`list_${active}_name`)}
          </div>
          <div className="text-[11px] text-neutral-500 truncate mt-0.5">
            {t(`list_${active}_subtitle`)}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-red-300 bg-red-500/10 ring-1 ring-red-500/30 rounded px-1.5 py-0.5 whitespace-nowrap">
          {t(`list_${active}_chip`)}
        </span>
      </div>

      {/* Rules */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 8 }}
          transition={{ duration: 0.3 }}
          className="space-y-2 flex-1"
        >
          {[1, 2, 3, 4].map((ruleIdx, i) => (
            <React.Fragment key={ruleIdx}>
              <RulePill text={t(`list_${active}_rule_${ruleIdx}`)} />
              {i < 3 && (
                <div className="flex items-center gap-2 px-1">
                  <span className="h-px flex-1 bg-neutral-800" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-red-400/70">
                    {t("and_label")}
                  </span>
                  <span className="h-px flex-1 bg-neutral-800" />
                </div>
              )}
            </React.Fragment>
          ))}
        </motion.div>
      </AnimatePresence>

      <button
        type="button"
        className="mt-4 text-[11px] font-medium text-neutral-500 hover:text-neutral-300 self-start"
      >
        {t("add_rule")}
      </button>
    </div>
  );
};

const RulePill = ({ text }: { text: string }) => (
  <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2.5">
    <span className="flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" />
    <span className="text-sm text-neutral-200 font-medium truncate">{text}</span>
  </div>
);

// ----------------------------------------------------------------------------
// Preview pane (right)
// ----------------------------------------------------------------------------

const PreviewPane = ({ active }: { active: ListKey }) => {
  const t = useTranslations("customLists");
  const rows = LEAD_ROWS[active];

  return (
    <div className="lg:col-span-7 relative overflow-hidden p-5 bg-neutral-950 flex flex-col">
      {/* Header with counts */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <FlameIcon className="h-3.5 w-3.5 text-red-500" />
            <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
              {t("preview_label")}
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <CountStat
              key={`found-${active}`}
              value={t(`list_${active}_found`)}
              label={t("found_label")}
              accent
            />
            <span className="text-neutral-700">/</span>
            <CountStat
              key={`avail-${active}`}
              value={t(`list_${active}_available`)}
              label={t("available_label")}
            />
          </div>
        </div>
        <button
          type="button"
          className="text-[11px] font-semibold uppercase tracking-wider text-red-200 bg-red-500/15 ring-1 ring-red-500/40 rounded-md px-3 py-2 hover:bg-red-500/25 transition-colors flex-shrink-0"
        >
          {t("play_button")}
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-neutral-800 bg-neutral-900/40 overflow-hidden flex-1 flex flex-col">
        <div className="grid grid-cols-12 gap-3 px-3 py-2 border-b border-neutral-800 text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
          <div className="col-span-6">{t("col_lead")}</div>
          <div className="col-span-4">{t("col_meta")}</div>
          <div className="col-span-2 text-right">{t("col_rating")}</div>
        </div>
        <AnimatePresence mode="wait">
          <motion.ul
            key={active}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="divide-y divide-neutral-800/60"
          >
            {rows.map((row, i) => (
              <LeadRowCell
                key={`${active}-${i}`}
                row={row}
                listKey={active}
                index={i}
              />
            ))}
          </motion.ul>
        </AnimatePresence>
      </div>

      {/* Claim meta */}
      <div className="mt-3 flex items-center gap-2">
        <LockIcon className="h-3.5 w-3.5 text-emerald-500" />
        <span className="text-[10px] text-neutral-500 truncate">
          {t("claim_meta")}
        </span>
      </div>
    </div>
  );
};

const CountStat = ({
  value,
  label,
  accent,
}: {
  value: string;
  label: string;
  accent?: boolean;
}) => (
  <motion.div
    initial={{ opacity: 0.6, y: -3 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="flex items-baseline gap-1.5"
  >
    <span
      className={cn(
        "text-2xl md:text-3xl font-bold tabular-nums",
        accent
          ? "text-red-500"
          : "text-neutral-400"
      )}
    >
      {value}
    </span>
    <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
      {label}
    </span>
  </motion.div>
);

const LeadRowCell = ({
  row,
  listKey,
  index,
}: {
  row: LeadRow;
  listKey: ListKey;
  index: number;
}) => {
  const t = useTranslations("customLists");
  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 + index * 0.05 }}
      className="grid grid-cols-12 gap-3 items-center px-3 py-2.5 hover:bg-neutral-900/40"
    >
      <div className="col-span-6 flex items-center gap-2.5 min-w-0">
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-neutral-800 text-[10px] font-semibold text-neutral-300 ring-1 ring-neutral-700">
          {row.initials}
        </span>
        <div className="min-w-0">
          <div className="text-sm text-neutral-200 font-medium truncate">
            {row.name}
          </div>
        </div>
      </div>
      <div className="col-span-4 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-red-300 bg-red-500/10 ring-1 ring-red-500/30 rounded px-1.5 py-0.5 whitespace-nowrap">
            {t(`list_${listKey}_chip`)}
          </span>
          <span className="text-[11px] text-neutral-500 truncate">
            {row.region}
          </span>
        </div>
      </div>
      <div className="col-span-2 text-right">
        <span className="text-[11px] font-mono tabular-nums text-neutral-300">
          {row.rating}
        </span>
      </div>
    </motion.li>
  );
};

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconFilter /> },
  { key: "t2", icon: <IconLock /> },
  { key: "t3", icon: <IconShield /> },
  { key: "t4", icon: <IconRefresh /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("customLists");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M3 5h18l-7 9v6l-4-2v-4z" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M3 5h18l-7 9v6l-4-2v-4z" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <path d="M21 4v5h-5" />
    </svg>
  );
}
